import { useId } from "react";
import type { AccountRecord } from "../lib/explainTypes";
import { CHECKPOINT_COLUMNS } from "../lib/explainTypes";
import { useAccountResolver } from "../hooks/useAccountResolver";
import { useDisplayMode, type DisplayMode } from "../hooks/useDisplayMode";
import { formatCurrency } from "../../../lib/numbers";

interface Props {
  account: AccountRecord;
}

// Per-symbol matrix for one account: checkpoint allocations across the
// algorithm transitions, plus the resulting trade (delta / shares /
// amount) and a totals footer.
//
// The title band carries a small %/$ toggle. Mode is shared across the
// whole explain page via `DisplayModeContext`, so flipping any one
// account's toggle re-renders all the others to match.
export default function SummaryRow({ account }: Props) {
  const resolve = useAccountResolver();
  const r = resolve(account);
  const { mode, setMode } = useDisplayMode();
  const balance = r.balance ?? 0;
  // SummaryRow renders twice per account (top Summary + AccountBlock
  // inline copy). useId() gives each instance its own DOM id prefix
  // so the radio inputs and their <label for=...> bindings stay
  // unique -- otherwise clicking the inline toggle activates the top
  // Summary's input and the browser scrolls the page back up.
  const uid = useId();

  // Collect every symbol that shows up in any checkpoint OR in trades.
  const allSymbols = new Set<string>();
  for (const key of CHECKPOINT_COLUMNS) {
    const cp = account.checkpoints[key];
    if (cp) for (const sym of Object.keys(cp.by_symbol)) allSymbols.add(sym);
  }
  for (const t of account.trade_generation?.trades ?? []) {
    allSymbols.add(t.symbol);
  }

  const symbols = Array.from(allSymbols).sort();
  const tradeBySymbol = new Map(
    (account.trade_generation?.trades ?? []).map((t) => [t.symbol, t]),
  );
  const colSpanFull = CHECKPOINT_COLUMNS.length + 4; // symbol + checkpoints + Δ + shares + amount

  const num = (s: string | undefined): number =>
    s == null || s === "" ? 0 : Number(s);

  // Weight cells show the raw decimal in weight mode and the dollar
  // equivalent (weight * balance) in dollar mode. Δ and totals get the
  // same treatment so the Δ column reads as either weight delta or $
  // delta, and the Total row sums to ~1.0 / ~account balance.
  const fmtWeight = (raw: string | undefined): string => {
    if (raw == null) return "—";
    return mode === "dollar" ? formatCurrency(num(raw) * balance) : raw;
  };
  const fmtDelta = (d: number): string =>
    mode === "dollar" ? formatCurrency(d * balance) : d.toFixed(4);
  const fmtTotal = (sum: number): string =>
    mode === "dollar" ? formatCurrency(sum * balance) : sum.toFixed(4);

  const checkpointTotals: Record<string, number> = {};
  for (const key of CHECKPOINT_COLUMNS) checkpointTotals[key] = 0;
  let deltaTotal = 0;
  let sharesTotal = 0;
  let amountTotal = 0;

  return (
    <>
      <tr>
        <th colSpan={colSpanFull} className="bg-secondary text-white px-3 py-2">
          <div className="d-flex align-items-center gap-3">
            <a
              href={`#account-${account.account_id}`}
              className="text-white text-decoration-none"
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById(`account-${account.account_id}`)
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            >
              {r.name}
              {r.number && (
                <span className="ms-2 fw-normal opacity-75">({r.number})</span>
              )}
            </a>
            <div
              role="radiogroup"
              aria-label="Display mode"
              className="d-inline-flex rounded-pill me-auto"
              style={{
                border: "1px solid rgba(255,255,255,0.2)",
                fontSize: "0.65rem",
                lineHeight: 1,
                overflow: "hidden",
                userSelect: "none",
              }}
            >
              {(["weight", "dollar"] as const).map((m) => {
                const active = mode === m;
                return (
                  <button
                    key={m}
                    id={`${uid}-${m === "weight" ? "w" : "d"}`}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setMode(m)}
                    className="border-0"
                    style={{
                      padding: "1px 7px",
                      background: active
                        ? "rgba(255,255,255,0.85)"
                        : "transparent",
                      color: active
                        ? "var(--bs-secondary, #6c757d)"
                        : "rgba(255,255,255,0.55)",
                      fontWeight: active ? 600 : 400,
                      transition: "background 80ms, color 80ms",
                    }}
                  >
                    {m === "weight" ? "%" : "$"}
                  </button>
                );
              })}
            </div>
            <span className="fw-normal opacity-75">
              {r.balance != null ? formatCurrency(r.balance) : null}
            </span>
          </div>
        </th>
      </tr>
      <tr>
        <th className="border-bottom">Symbol</th>
        {CHECKPOINT_COLUMNS.map((c) => (
          <th key={c} className="text-end text-capitalize border-bottom">
            {c}
          </th>
        ))}
        <th className="text-end border-bottom">Δ</th>
        <th className="text-end border-bottom">Shares</th>
        <th className="text-end border-bottom">Amount</th>
      </tr>
      {symbols.map((sym) => {
        const trade = tradeBySymbol.get(sym);
        const holdingsWeight = num(
          account.checkpoints.holdings?.by_symbol[sym],
        );
        const finalWeight = num(account.checkpoints.final?.by_symbol[sym]);
        const delta = finalWeight - holdingsWeight;
        const sharesSigned =
          trade != null
            ? (trade.side === "buy" ? 1 : -1) * num(trade.shares)
            : 0;
        const amountSigned =
          trade != null
            ? (trade.side === "buy" ? 1 : -1) * num(trade.value)
            : 0;

        deltaTotal += delta;
        sharesTotal += sharesSigned;
        amountTotal += amountSigned;

        return (
          <tr key={`${account.account_id}-${sym}`}>
            <td>{sym}</td>
            {CHECKPOINT_COLUMNS.map((col) => {
              const value = account.checkpoints[col]?.by_symbol[sym];
              checkpointTotals[col] += num(value);
              return (
                <td key={col} className="text-end">
                  {fmtWeight(value)}
                </td>
              );
            })}
            <td className="text-end">{fmtDelta(delta)}</td>
            <td className="text-end">{trade ? sharesSigned : "—"}</td>
            <td className="text-end">
              {trade ? formatCurrency(amountSigned) : "—"}
            </td>
          </tr>
        );
      })}
      <tr className="fw-bold border-top">
        <td className="border-bottom-0">Total</td>
        {CHECKPOINT_COLUMNS.map((col) => (
          <td key={col} className="text-end border-bottom-0">
            {fmtTotal(checkpointTotals[col])}
          </td>
        ))}
        <td className="text-end border-bottom-0">{fmtDelta(deltaTotal)}</td>
        <td className="text-end border-bottom-0">{sharesTotal}</td>
        <td className="text-end border-bottom-0">
          {formatCurrency(amountTotal)}
        </td>
      </tr>
      {/* Spacer between accounts -- gives the totals row breathing room
          before the next account's title band. */}
      <tr aria-hidden="true">
        <td colSpan={colSpanFull} className="border-0 p-2"></td>
      </tr>
    </>
  );
}
